'use client';

import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  FileXls,
  FileCsv,
  Upload,
  CheckCircle,
  Spinner,
  X,
  Warning,
} from '@phosphor-icons/react';
import { useUploadedFileStore } from '../../../stores/uploadedFileStore';
import { useSchema } from '../../../contexts/SchemaContext';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { unstable_batchedUpdates } from 'react-dom';
import type { Column, DataType } from '../../../types';

interface FileUploadWidgetProps {
  nodeId: string;
  /** 'excel' 限制只接收 .xlsx/.xls; 'csv' 只接收 .csv; 'both' 两者均接 */
  accept: 'excel' | 'csv' | 'both';
  /** DAG operator type written to config (default: load_excel / load_csv from accept) */
  persistType?: string;
  /** Merged into saved params on upload/clear */
  paramDefaults?: Record<string, unknown>;
  /** 表头起始行（1-based），仅 Excel 有效；默认 1 */
  headerRow?: number;
  /** 上传成功后回调（用于更新节点 config 里的 fileName 参数） */
  onUploaded?: (fileName: string, rowCount: number, columns: string[]) => void;
  /** 当自动检测到表头行变化时的回调 */
  onHeaderRowDetected?: (headerRow: number) => void;
}

function inferDataType(values: unknown[]): DataType {
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (nonNull.length === 0) return 'string';
  if (nonNull.every((v) => typeof v === 'number' || !isNaN(Number(v)))) return 'number';
  if (nonNull.every((v) => /^\d{4}[-/]\d{2}[-/]\d{2}/.test(String(v)))) return 'date';
  return 'string';
}

/**
 * Normalize a raw cell value from xlsx into a displayable string / primitive.
 *
 * xlsx returns:
 *  - Date objects when cellDates:true is used and the cell has a date format
 *  - numbers for numeric cells
 *  - strings for text cells
 *  - booleans for boolean cells
 *
 * Dates are formatted as "YYYY-MM-DD" (date-only) or "YYYY-MM-DD HH:mm:ss"
 * (when the time component is non-zero), matching the Go server output.
 */
function normalizeCellValue(val: unknown): unknown {
  if (val instanceof Date) {
    const y = val.getFullYear();
    const mo = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    const h = val.getHours();
    const mi = val.getMinutes();
    const s = val.getSeconds();
    if (h === 0 && mi === 0 && s === 0) {
      return `${y}-${mo}-${d}`;
    }
    return `${y}-${mo}-${d} ${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return val;
}

const ACCEPT_MAP = {
  excel: '.xlsx,.xls',
  csv: '.csv',
  both: '.xlsx,.xls,.csv',
};

const ACCEPT_REGEX: Record<FileUploadWidgetProps['accept'], RegExp> = {
  excel: /\.(xlsx|xls)$/i,
  csv: /\.csv$/i,
  both: /\.(xlsx|xls|csv)$/i,
};

/**
 * 检测工作表中是否存在合并单元格
 * @param sheet - XLSX 工作表对象
 * @returns boolean - 是否存在合并单元格
 */
function hasMergedCells(sheet: XLSX.WorkSheet): boolean {
  return !!(sheet['!merges'] && sheet['!merges'].length > 0);
}

/**
 * 检测某一行是否包含合并单元格
 * @param sheet - XLSX 工作表对象
 * @param rowIndex - 行索引（0-based）
 * @returns boolean - 该行是否包含合并单元格
 */
function rowHasMergedCells(sheet: XLSX.WorkSheet, rowIndex: number): boolean {
  if (!sheet['!merges']) return false;
  return sheet['!merges'].some((merge) => {
    const startRow = merge.s.r;
    const endRow = merge.e.r;
    return rowIndex >= startRow && rowIndex <= endRow;
  });
}

/**
 * 自动识别表头行 - 查找第一个没有合并单元格的行
 * @param sheet - XLSX 工作表对象
 * @returns number - 表头行号（1-based），如果没有合并单元格则返回 1
 */
function detectHeaderRow(sheet: XLSX.WorkSheet): number {
  // 如果没有合并单元格，默认第一行为表头
  if (!hasMergedCells(sheet)) return 1;

  // 获取工作表的有效范围
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const totalRows = range.e.r + 1;

  // 遍历每一行，查找第一个没有合并单元格的行
  for (let rowIdx = 0; rowIdx < Math.min(totalRows, 50); rowIdx++) {
    if (!rowHasMergedCells(sheet, rowIdx)) {
      return rowIdx + 1; // 转换为 1-based 行号
    }
  }

  // 如果前 50 行都有合并单元格，默认返回第一行
  return 1;
}

export function FileUploadWidget({ nodeId, accept, persistType, paramDefaults, headerRow: headerRowProp = 1, onUploaded, onHeaderRowDetected }: FileUploadWidgetProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { files, setFileForNode, setOriginalExcelFile, clearFileForNode, getOriginalExcelFile, isUploading, setIsUploading } = useUploadedFileStore();
  const { loadSchemaFromDataFile, unregisterNodeSchema } = useSchema();
  const { updateNodeConfig } = useWorkflowStore();

  const uploadedFile = files[nodeId] ?? null;
  const headerRow = Math.max(1, headerRowProp || 1);

  const processFile = async (file: File) => {
    setError(null);
    if (!ACCEPT_REGEX[accept].test(file.name)) {
      setError(t('nodes.config.load.unsupportedFormat', { formats: ACCEPT_MAP[accept] }));
      return;
    }

    setIsUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<unknown[]>(firstSheet, { header: 1 });

      const isExcel = ACCEPT_REGEX.excel.test(file.name);
      
      // 自动检测表头行（仅对 Excel 文件）
      let detectedHeaderRow = headerRow;
      if (isExcel) {
        detectedHeaderRow = detectHeaderRow(firstSheet);
        // 如果检测到的表头行与当前设置不同，通知父组件更新
        if (detectedHeaderRow !== headerRow && onHeaderRowDetected) {
          onHeaderRowDetected(detectedHeaderRow);
        }
      }
      
      const headerIdx = isExcel ? Math.max(0, detectedHeaderRow - 1) : 0;
      const minRows = isExcel ? headerIdx + 2 : 2; // header + at least one data row
      if (jsonData.length < minRows) {
        setError(t('nodes.config.load.insufficientContent'));
        return;
      }

      const headers = (jsonData[headerIdx] as unknown[]).map((h) => String(h).trim());
      const dataStart = headerIdx + 1;
      const dataRows = jsonData.slice(dataStart).map((row) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((h, i) => {
          obj[h] = normalizeCellValue((row as unknown[])[i] ?? null);
        });
        return obj;
      });

      const columns: Column[] = headers.map((name, idx) => ({
        name,
        type: inferDataType(dataRows.map((r) => r[name])),
        nullable: dataRows.some((r) => r[name] === null || r[name] === undefined || r[name] === ''),
        index: idx,
      }));

      const fileId = `file_${nodeId}`;
      const nodeType = persistType ?? (accept === 'csv' ? 'load_csv' : 'load_excel');
      const params: Record<string, unknown> = { ...paramDefaults, fileName: file.name, rowCount: dataRows.length };
      if (isExcel) (params as Record<string, number>).headerRow = detectedHeaderRow;

      unstable_batchedUpdates(() => {
        setFileForNode(nodeId, { id: fileId, name: file.name, rowCount: dataRows.length, columns: headers, data: dataRows });
        if (isExcel) setOriginalExcelFile(nodeId, file);
        else setOriginalExcelFile(nodeId, null);
        loadSchemaFromDataFile(nodeId, fileId, file.name, columns);
        updateNodeConfig(nodeId, { type: nodeType, params });
      });
      onUploaded?.(file.name, dataRows.length, headers);
    } catch {
      setError(t('nodes.config.load.parseError'));
    } finally {
      setIsUploading(false);
    }
  };

  // When headerRow changes and we already have an uploaded Excel file, re-parse with new header row (skip on initial mount)
  const prevHeaderRowRef = useRef(headerRow);
  useEffect(() => {
    if (accept !== 'excel' || !uploadedFile) return;
    if (prevHeaderRowRef.current === headerRow) return;
    prevHeaderRowRef.current = headerRow;
    const original = getOriginalExcelFile(nodeId);
    if (original) processFile(original);
  }, [headerRow, accept, uploadedFile, nodeId, getOriginalExcelFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleClear = () => {
    unstable_batchedUpdates(() => {
      clearFileForNode(nodeId);
      unregisterNodeSchema(nodeId);
      updateNodeConfig(nodeId, {
        type: persistType ?? (accept === 'csv' ? 'load_csv' : 'load_excel'),
        params: { ...paramDefaults },
      });
    });
  };

  const isThisNodeFile = !!uploadedFile;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-500">{t('nodes.config.load.dataFile')}</span>
        {isThisNodeFile && (
          <button
            onClick={handleClear}
            className="text-[10px] text-red-400 hover:text-red-600 transition-colors"
          >
            {t('common.clear')}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isThisNodeFile && uploadedFile ? (
          /* ── Uploaded state ── */
          <motion.div
            key="uploaded"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100"
          >
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              {accept === 'csv'
                ? <FileCsv className="w-5 h-5 text-emerald-600" weight="fill" />
                : <FileXls className="w-5 h-5 text-emerald-600" weight="fill" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-emerald-900 truncate">{uploadedFile.name}</p>
              <p className="text-[10px] text-emerald-600">
                {uploadedFile.rowCount.toLocaleString()} {t('nodes.rows')} · {uploadedFile.columns.length} {t('nodes.columns')}
              </p>
            </div>
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" weight="fill" />
          </motion.div>
        ) : (
          /* ── Upload zone ── */
          <motion.div
            key="upload-zone"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
          >
            <div
              className={[
                'relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200',
                isDragging
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50/30',
              ].join(' ')}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-2">
                {isUploading ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                    <Spinner className="w-7 h-7 text-emerald-400" weight="bold" />
                  </motion.div>
                ) : (
                  <Upload className="w-7 h-7 text-zinc-300" />
                )}
                <div>
                  <p className="text-xs font-medium text-zinc-600">
                    {isUploading ? t('common.parsing') : t('nodes.config.load.dragTip')}
                  </p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{t('nodes.config.load.supports', { formats: ACCEPT_MAP[accept] })}</p>
                </div>
              </div>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 mt-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg overflow-hidden"
                >
                  <Warning className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <p className="text-[11px] text-red-600 flex-1">{error}</p>
                  <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_MAP[accept]}
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
}
